import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, InputGroup, Modal, Row, Spinner } from "react-bootstrap";
import { FiAlertTriangle, FiCheck, FiImage, FiPlus, FiSearch, FiShield, FiUser, FiX } from "react-icons/fi";
import { getHost } from "../utils/utils";
import { AccessPill, CATEGORY_COLORS, ClientAvatar, flag, fromHex, toHex } from "./clientUi";
import "./AdminClients.css";

const API_CLIENTS = `${getHost()}/api/admin/clients`;
const getToken = () => localStorage.getItem("jwt");

const emptyForm = {
  name: "",
  user_ids: [],
  avatar: "",
  color: "#ffffff",
  footer_text: "",
  footer_icon: "",
  use_group_name: false,
  category: [],
  monitors: [],
};

const revokeCount = (preview) =>
  preview ? preview.summary.webhooks + preview.summary.forwards_deleted + preview.summary.forwards_updated : 0;

const RevokeDetails = ({ preview }) => (
  <>
    <p className="text-main fs-14 mb-2">
      <b>{preview.summary.webhooks}</b> webhook{preview.summary.webhooks === 1 ? "" : "s"} on{" "}
      <b>{preview.monitors.filter((m) => m.webhooks > 0).length}</b> monitor
      {preview.monitors.filter((m) => m.webhooks > 0).length === 1 ? "" : "s"}
      {preview.forwards.length > 0 && (
        <>
          {" "}and <b>{preview.forwards.length}</b> forward{preview.forwards.length === 1 ? "" : "s"}
        </>
      )}{" "}
      will be deleted when you save.
    </p>
    <ul className="revoke-list list-unstyled mb-0">
      {preview.monitors
        .filter((m) => m.webhooks > 0)
        .map((m) => (
          <li key={m._id} className="d-flex justify-content-between py-1">
            <span>{m.name}</span>
            <span className="text-red">
              {m.webhooks} webhook{m.webhooks === 1 ? "" : "s"}
            </span>
          </li>
        ))}
      {preview.forwards.map((f) => (
        <li key={f._id} className="d-flex justify-content-between py-1">
          <span>
            Forward {f.keywords.length > 0 ? `“${f.keywords.slice(0, 3).join(", ")}”` : ""} on {f.lost.join(", ")}
          </span>
          <span className="text-red">{f.action === "delete" ? "deleted" : "site removed"}</span>
        </li>
      ))}
    </ul>
  </>
);

const ClientModal = ({ show, onHide, client, categories, monitors, onSaved }) => {
  const [form, setForm] = useState(emptyForm);
  const [userInput, setUserInput] = useState("");
  const [search, setSearch] = useState("");
  const [showDisabled, setShowDisabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [pending, setPending] = useState(null);
  const [checking, setChecking] = useState(false);
  const [acknowledged, setAcknowledged] = useState(null);

  useEffect(() => {
    if (!show) return;
    setError("");
    setSearch("");
    setUserInput("");
    setPending(null);
    setAcknowledged(null);
    setForm(
      client
        ? {
            name: client.name,
            user_ids: client.user_ids,
            avatar: client.avatar,
            color: toHex(client.color),
            footer_text: client.footer?.text || "",
            footer_icon: client.footer?.icon_url || "",
            use_group_name: client.use_group_name,
            category: client.category,
            monitors: client.monitors,
          }
        : emptyForm,
    );
  }, [show, client]);

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const monitorById = useMemo(() => new Map(monitors.map((m) => [m._id, m])), [monitors]);
  const covered = (m, cats = form.category) => cats.includes(m.category);
  const selected = new Set(form.monitors);

  const accessBody = (next) => ({
    category: next.category,
    monitors: next.monitors.filter((id) => {
      const m = monitorById.get(id);
      return !m || !covered(m, next.category);
    }),
  });

  // Applies an access change; removals on an existing client are checked first.
  const applyAccess = async (next, isRemoval) => {
    if (!client || !isRemoval) {
      setForm((f) => ({ ...f, ...next }));
      return;
    }
    setChecking(true);
    try {
      const res = await fetch(`${API_CLIENTS}/${client._id}/access-preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(accessBody({ ...form, ...next })),
      });
      const preview = await res.json();
      if (res.ok && revokeCount(preview) > revokeCount(acknowledged)) {
        setPending({ next, preview });
      } else {
        setForm((f) => ({ ...f, ...next }));
      }
    } catch {
      setError("Could not check what this removal deletes");
    } finally {
      setChecking(false);
    }
  };

  const toggleCategory = (cat) => {
    const on = form.category.includes(cat);
    applyAccess({ category: on ? form.category.filter((c) => c !== cat) : [...form.category, cat] }, on);
  };

  const toggleSites = (ids) => {
    const free = ids.filter((id) => !covered(monitorById.get(id) || {}));
    const allOn = free.every((id) => selected.has(id));
    applyAccess(
      { monitors: allOn ? form.monitors.filter((id) => !free.includes(id)) : [...new Set([...form.monitors, ...free])] },
      allOn,
    );
  };

  const countByCategory = useMemo(() => {
    const out = {};
    monitors.forEach((m) => {
      if (m.enabled) out[m.category] = (out[m.category] || 0) + 1;
    });
    return out;
  }, [monitors]);

  const sections = useMemo(() => {
    const q = search.trim().toLowerCase();
    const byCat = new Map();
    monitors
      .filter((m) => showDisabled || m.enabled || selected.has(m._id))
      .filter((m) => !q || m.name.toLowerCase().includes(q) || (m.family || "").includes(q) || (m.region || "").toLowerCase() === q)
      .forEach((m) => {
        const cat = m.category || "Uncategorised";
        if (!byCat.has(cat)) byCat.set(cat, new Map());
        const key = m.family || m.name;
        const groups = byCat.get(cat);
        if (!groups.has(key)) groups.set(key, { key, label: m.family ? m.family : m.name, items: [] });
        groups.get(key).items.push(m);
      });
    const order = [...categories, "Uncategorised"];
    return [...byCat.entries()]
      .sort(([a], [b]) => order.indexOf(a) - order.indexOf(b))
      .map(([cat, groups]) => ({
        category: cat,
        groups: [...groups.values()].sort((a, b) => a.label.localeCompare(b.label)),
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monitors, search, showDisabled, categories, form.monitors]);

  const addUsers = (raw) => {
    const ids = raw.split(/[\s,]+/).map((u) => u.trim()).filter(Boolean);
    if (!ids.length) return;
    set("user_ids", [...new Set([...form.user_ids, ...ids])]);
    setUserInput("");
  };

  const explicitSites = form.monitors.filter((id) => !covered(monitorById.get(id) || {}));

  const save = async (confirm) => {
    setSaving(true);
    setError("");
    const pendingUsers = userInput.split(/[\s,]+/).map((u) => u.trim()).filter(Boolean);
    try {
      const res = await fetch(client ? `${API_CLIENTS}/${client._id}` : API_CLIENTS, {
        method: client ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          name: form.name.trim(),
          user_ids: [...new Set([...form.user_ids, ...pendingUsers])],
          avatar: form.avatar.trim(),
          color: fromHex(form.color),
          footer: { text: form.footer_text.trim() || form.name.trim(), icon_url: form.footer_icon.trim() },
          use_group_name: form.use_group_name,
          ...accessBody(form),
          ...(confirm ? { confirm: true } : {}),
        }),
      });
      const data = await res.json();
      if (res.status === 409 && data.error === "confirmation_required") {
        setPending({ next: {}, preview: data, onSave: true });
        return;
      }
      if (!res.ok) {
        setError(data.error || "Save failed");
        return;
      }
      onSaved();
    } catch {
      setError("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const embedColor = form.color;
  const senderName = form.use_group_name ? form.name || "Client" : "Ticketmaster DE";

  return (
    <>
      <Modal show={show && !pending} onHide={onHide} centered className="client-modal">
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title className="fs-18 text-main d-flex align-items-center gap-2">
            {client && <ClientAvatar src={form.avatar} name={form.name} color={fromHex(form.color)} size={28} />}
            {client ? client.name : "New client"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="client-section-title">
            <FiUser /> Client
          </div>
          <Row className="g-3 mb-4">
            <Col md={5}>
              <div className="client-label">Name</div>
              <Form.Control
                className="form-input-field"
                placeholder="e.g. Ticketwave Monitors"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
              />
            </Col>
            <Col md={7}>
              <div className="client-label">Discord users ({form.user_ids.length})</div>
              <div className="chips-input">
                {form.user_ids.map((u) => (
                  <span key={u} className="user-chip">
                    {u}
                    <button
                      type="button"
                      className="chip-remove"
                      onClick={() => set("user_ids", form.user_ids.filter((x) => x !== u))}
                    >
                      <FiX />
                    </button>
                  </span>
                ))}
                <input
                  value={userInput}
                  placeholder="Paste a Discord user id, Enter"
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      addUsers(userInput);
                    }
                  }}
                  onBlur={() => addUsers(userInput)}
                />
              </div>
            </Col>
          </Row>

          <div className="client-section-title">
            <FiImage /> Webhook look
          </div>
          <Row className="g-3 mb-4">
            <Col md={6}>
              <div className="client-label">Logo URL</div>
              <Form.Control
                className="form-input-field mb-3"
                placeholder="https://..."
                value={form.avatar}
                onChange={(e) => set("avatar", e.target.value)}
              />
              <div className="client-label">Footer text</div>
              <Form.Control
                className="form-input-field mb-3"
                placeholder={form.name || "Footer"}
                value={form.footer_text}
                onChange={(e) => set("footer_text", e.target.value)}
              />
              <div className="client-label">Footer icon URL</div>
              <InputGroup className="mb-3">
                <Form.Control
                  className="form-input-field"
                  placeholder="https://..."
                  value={form.footer_icon}
                  onChange={(e) => set("footer_icon", e.target.value)}
                />
                <Button variant="dark" className="border-secondary fs-13" onClick={() => set("footer_icon", form.avatar)}>
                  Same as logo
                </Button>
              </InputGroup>
              <div className="d-flex align-items-center gap-4">
                <div className="d-flex align-items-center gap-2">
                  <Form.Control
                    type="color"
                    className="form-control-color p-1"
                    value={form.color}
                    onChange={(e) => set("color", e.target.value)}
                  />
                  <span className="fs-13 font-monospace">{form.color.toUpperCase()}</span>
                </div>
                <Form.Check
                  type="switch"
                  id="use-group-name"
                  className="fs-13"
                  label="Send as client name"
                  checked={form.use_group_name}
                  onChange={(e) => set("use_group_name", e.target.checked)}
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="client-label">Preview</div>
              <div className="embed-preview">
                <ClientAvatar src={form.avatar} name={form.name} color={fromHex(form.color)} size={40} />
                <div className="flex-grow-1 min-w-0">
                  <div className="mb-1">
                    <span className="embed-name">{senderName}</span>
                    <span className="embed-bot">APP</span>
                  </div>
                  <div className="embed-card" style={{ borderColor: embedColor }}>
                    <div className="embed-title">Champions League Final — Tickets</div>
                    <div className="fs-13 mt-1" style={{ color: "#DBDEE1" }}>
                      Price <b>€180</b> · Stock <b>In stock</b>
                    </div>
                    <div className="embed-footer">
                      {form.footer_icon && <img src={form.footer_icon} alt="" onError={(e) => (e.target.style.display = "none")} />}
                      <span>{form.footer_text || form.name || "Footer"}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          <div className="client-section-title">
            <FiShield /> Access
          </div>
          <div className="client-label">Categories — every monitor in the category</div>
          <div className="d-flex flex-wrap gap-2 mb-3">
            {categories.map((cat) => {
              const active = form.category.includes(cat);
              return (
                <button
                  key={cat}
                  type="button"
                  disabled={checking}
                  className={`category-toggle ${active ? "active" : ""}`}
                  onClick={() => toggleCategory(cat)}
                >
                  <span className="access-dot" style={{ background: CATEGORY_COLORS[cat] }} />
                  {cat}
                  <span className="count">{countByCategory[cat] || 0}</span>
                </button>
              );
            })}
          </div>

          <div className="d-flex flex-wrap justify-content-between align-items-end gap-2 mb-2">
            <div className="client-label mb-0">
              Sites — single monitors on top of the categories ({explicitSites.length} selected)
            </div>
            <div className="d-flex align-items-center gap-3">
              <Form.Check
                type="switch"
                id="show-disabled"
                className="fs-13"
                label="Show disabled"
                checked={showDisabled}
                onChange={(e) => setShowDisabled(e.target.checked)}
              />
              <InputGroup size="sm" style={{ width: 230 }}>
                <InputGroup.Text className="bg-dark border-secondary">
                  <FiSearch className="text-gray" />
                </InputGroup.Text>
                <Form.Control
                  className="form-input-field border-secondary"
                  placeholder="Search sites, family, region..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </InputGroup>
            </div>
          </div>

          <div className="sites-picker">
            <div className="sites-list">
              {sections.length === 0 && <div className="text-center py-4 fs-13">No sites match</div>}
              {sections.map((section) => {
                const isCovered = form.category.includes(section.category);
                return (
                  <div key={section.category}>
                    <div className="sites-category">
                      <span className="d-flex align-items-center gap-2">
                        <span className="access-dot" style={{ background: CATEGORY_COLORS[section.category] || "#848484" }} />
                        {section.category}
                      </span>
                      {isCovered && <span className="text-green fw-500">Covered by category</span>}
                    </div>
                    {section.groups.map((g) => {
                      const ids = g.items.map((m) => m._id);
                      const free = g.items.filter((m) => !covered(m));
                      const allOn = free.length > 0 && free.every((m) => selected.has(m._id));
                      if (g.items.length === 1) {
                        const m = g.items[0];
                        const on = covered(m) || selected.has(m._id);
                        return (
                          <div key={g.key} className="sites-group d-flex justify-content-between align-items-center">
                            <span className="text-main fs-14">
                              {m.name}
                              {m.region && <span className="text-white-65 fs-13 ms-2">{flag(m.region)} {m.region}</span>}
                              {!m.enabled && <span className="text-white-65 fs-12"> · disabled</span>}
                            </span>
                            <button
                              type="button"
                              className={`site-option ${on ? "selected" : ""}`}
                              disabled={covered(m) || checking}
                              onClick={() => toggleSites([m._id])}
                            >
                              {on ? <FiCheck /> : <FiPlus />}
                              {covered(m) ? "Included" : on ? "Added" : "Add"}
                            </button>
                          </div>
                        );
                      }
                      return (
                        <div key={g.key} className="sites-group">
                          <div className="sites-group-head mb-2">
                            <span>
                              <span className="text-capitalize">{g.label}</span> <span className="text-white-65 fs-12">· {g.items.length} regions</span>
                            </span>
                            {free.length > 0 && (
                              <button type="button" className="link-btn" disabled={checking} onClick={() => toggleSites(ids)}>
                                {allOn ? "Clear all" : "Select all"}
                              </button>
                            )}
                          </div>
                          <div className="d-flex flex-wrap gap-2">
                            {g.items.map((m) => {
                              const on = covered(m) || selected.has(m._id);
                              return (
                                <button
                                  key={m._id}
                                  type="button"
                                  title={m.name}
                                  className={`site-option ${on ? "selected" : ""}`}
                                  disabled={covered(m) || checking}
                                  onClick={() => toggleSites([m._id])}
                                >
                                  {m.region ? `${flag(m.region)} ${m.region}` : m.name}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
            {explicitSites.length > 0 && (
              <div className="selected-sites">
                {explicitSites.map((id) => {
                  const m = monitorById.get(id);
                  return (
                    <AccessPill key={id} category={m?.category}>
                      {m?.name || id}
                      <button type="button" className="chip-remove" disabled={checking} onClick={() => toggleSites([id])}>
                        <FiX />
                      </button>
                    </AccessPill>
                  );
                })}
              </div>
            )}
          </div>

          {error && (
            <Alert variant="danger" className="mt-3 mb-0">
              {error}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          {revokeCount(acknowledged) > 0 && (
            <span className="text-red fs-13 me-auto d-flex align-items-center gap-2">
              <FiAlertTriangle /> {acknowledged.summary.webhooks} webhooks will be deleted on save
            </span>
          )}
          <Button className="btn-secondary rounded-3 py-1 px-3 fw-600 fs-12" onClick={onHide} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            className="btn-primary rounded-3 py-1 px-3 fw-600 fs-12"
            onClick={() => save(revokeCount(acknowledged) > 0)}
            disabled={saving || checking || !form.name.trim()}
          >
            {saving ? <Spinner size="sm" /> : client ? "Save" : "Create client"}
          </Button>
        </Modal.Footer>
        {checking && (
          <div className="client-modal-overlay">
            <Spinner animation="border" variant="light" />
            <div className="text-main fw-500 mt-3">Checking what this removal affects…</div>
            <div className="fs-13 mt-1">Looking for this client's webhooks and forwards on the sites it would lose.</div>
          </div>
        )}
      </Modal>

      <Modal show={show && !!pending} onHide={() => setPending(null)} centered>
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title className="fs-18 text-main d-flex align-items-center gap-2">
            <FiAlertTriangle className="text-red" /> Remove access?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>{pending && <RevokeDetails preview={pending.preview} />}</Modal.Body>
        <Modal.Footer>
          <Button className="btn-secondary rounded-3 py-1 px-3 fw-600 fs-12" onClick={() => setPending(null)}>
            Keep access
          </Button>
          <Button
            variant="danger"
            className="rounded-3 py-1 px-3 fw-600 fs-12"
            onClick={() => {
              const { next, preview, onSave } = pending;
              setPending(null);
              setAcknowledged(preview);
              if (onSave) save(true);
              else setForm((f) => ({ ...f, ...next }));
            }}
          >
            {pending?.onSave ? "Delete and save" : "Remove access"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ClientModal;
